import { useContext, useMemo, useState } from 'react'

import { AppContext } from 'providers/context/App'
import { conditionalClasses, formattedCost } from 'utils/helper-functions'
import { Facial } from 'utils/types/appTypes'
import { ServiceContent } from 'utils/types/maliblePageTypes'
import useMembership from 'views/hooks/useMembership'

type FacialServiceType = Facial & ServiceContent

interface FacialCardProps {
  selectedFacial: string
  facial: FacialServiceType
  onSelectFacial: (facial: FacialServiceType) => void
}

const FacialCard = ({
  selectedFacial,
  facial,
  onSelectFacial,
}: FacialCardProps) => {
  const { appMasterData } = useContext(AppContext)
  const { isMember, isMonthlyMember, isBiMonthlyMember } = useMembership()
  const [collapse, setCollapse] = useState(false)

  const baseMemberPrice = Math.max(
    appMasterData?.membershipData?.listPrice,
    8500
  )

  const checkLaserMicroCategory = ['Laser', 'Microneedling'].includes(
    facial.category
  )
  // isLaserCategory this will check later
  const isLaserCategory = facial.category === 'Laser' // NOSONAR
  const isMicroCategory = facial.category === 'Microneedling'
  const selectedTier =
    appMasterData.location.tier?.toLowerCase() as keyof FacialServiceType

  const memberPrice = useMemo(() => {
    if (!isMember || (checkLaserMicroCategory && !isMonthlyMember)) {
      return null
    }

    return formattedCost(
      Math.max(facial[selectedTier] as number, baseMemberPrice),
      false
    )
  }, [
    baseMemberPrice,
    isMember,
    isMonthlyMember,
    checkLaserMicroCategory,
    facial,
    selectedTier,
  ])

  const nonMemberPrice = useMemo(() => {
    if (isMember) return null
    return formattedCost(facial[selectedTier] as number, false)
  }, [isMember, facial, selectedTier])

  const facialDescription = collapse
    ? facial.description
    : `${facial.description?.slice(0, 80)} ...`

  const isSelected = selectedFacial === facial.name

  const originalPriceClass = conditionalClasses(
    isMember,
    conditionalClasses(
      !facial?.isMembershipPrice ||
        (checkLaserMicroCategory && !isMonthlyMember),
      '',
      'line-through'
    ),
    '',
    'original-price'
  )

  const showMemberPrice =
    isMember && memberPrice && (!isMicroCategory || !isBiMonthlyMember)

  const isSamePrice = facial[selectedTier] === facial.listPrice

  return (
    <button
      className={`facial-card ${isSelected && 'facial-selected'}`}
      key={facial.name}
      onClick={() => onSelectFacial(facial)}
      data-testid={`facial-service-${facial.id}`}
    >
      <div className="facial-details">
        <div className="facial-img-heading">
          <img className="facial-image" src={facial.icon} alt={facial.name} />
          <div className="facial-name-section">
            <h3 className="facial-name">{facial.name}</h3>
            {facial.label?.length > 1 && (
              <div className="new-text">{facial.label}</div>
            )}
          </div>
        </div>
        <div className="facial-data">
          <p className="facial-description">
            <span
              dangerouslySetInnerHTML={{
                __html: facialDescription,
              }}
            />
            {!collapse && (
              <span
                role="none"
                className="option-more-button"
                onClick={() => setCollapse(!collapse)}
              >
                LEARN MORE
              </span>
            )}
          </p>
          <div className="facial-price">
            <div className={originalPriceClass} data-testid="original-price">
              {formattedCost(facial.listPrice, false, 100)}
            </div>

            {facial.isMembershipPrice && (
              <>
                {nonMemberPrice && !isSamePrice && (
                  <div className="non-member">
                    {nonMemberPrice} MONTHLY MEMBER
                  </div>
                )}

                {showMemberPrice && !isSamePrice && (
                  <div className="member-price" data-testid="member-price">
                    {memberPrice}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

export default FacialCard
